import React, { useEffect, useRef } from 'react';

export interface IAudioVisualizerProps {
  analyserNode: AnalyserNode | null;
  width?: number;
  height?: number;
  steps?: number;
  normalizedVolumeScale?: number;
  disabled?: boolean;
}

export function AudioVisualizer({ analyserNode, width = 30, height = 50, steps = 10, normalizedVolumeScale = 100, disabled }: IAudioVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationIdRef = useRef<number | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const canvasCtx = canvas?.getContext('2d');

    const bufferLength = analyserNode?.frequencyBinCount ?? 0;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      if (!canvas || !canvasCtx) return;

      if (analyserNode && !disabled) {
        analyserNode.getByteFrequencyData(dataArray);
      }
      // Calculate average volume level (normalize to 0-1)
      const averageVolume = dataArray.reduce((acc, val) => acc + val, 0) / dataArray.length;
      const normalizedVolume = averageVolume / normalizedVolumeScale;
      const activeSteps = Math.round(normalizedVolume * steps);

      // Clear canvas
      canvasCtx.clearRect(0, 0, width, height);

      // Bar settings
      const barHeight = height / steps;
      for (let i = 0; i < steps; i += 1) {
        const y = height - (i + 1) * barHeight;

        // Color levels based on loudness
        let color = 'green';
        if (i >= steps * 0.7) color = 'red'; // Top 30% is red
        else if (i >= steps * 0.4) color = 'yellow'; // Middle 30% is yellow

        // Light up active bars, dim others
        canvasCtx.fillStyle = i < activeSteps ? color : 'rgba(50, 50, 50, 0.2)';
        canvasCtx.fillRect(0, y, width, barHeight - 2);
      }

      animationIdRef.current = requestAnimationFrame(draw);
    };

    draw();
    // eslint-disable-next-line consistent-return
    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
        animationIdRef.current = null;
      }
    };
  }, [analyserNode, width, height, disabled]);

  return <canvas ref={canvasRef} width={width} height={height} />;
}
